import styled from '@emotion/styled';

import { assertNever } from '../../common/utils';

export const iconOptions = ['start', 'end', 'icon only'] as const;
type IconOption = (typeof iconOptions)[number];
export const buttonSizes = [
  'extratiny',
  'xxsmall',
  'xsmall',
  'small',
  'medium',
  'large',
] as const;
export type ButtonSize = (typeof buttonSizes)[number];

interface ButtonProps {
  icon?: IconOption;
  size: ButtonSize;
  rounded?: boolean;
}

function sizeToPx(size: ButtonSize) {
  switch (size) {
    case 'extratiny':
      return 24;
    case 'xxsmall':
      return 32;
    case 'xsmall':
      return 40;
    case 'small':
      return 46;
    case 'medium':
      return 56;
    case 'large':
      return 64;
    default:
      return assertNever(size);
  }
}

function isSmallOrTiny(size: ButtonSize) {
  return ['extratiny', 'xxsmall', 'xsmall', 'small'].includes(size);
}

export const ButtonSecondary = styled('button', {
  shouldForwardProp: (prop) =>
    !['icon', 'size', 'rounded'].includes(String(prop)),
})<ButtonProps>`
  ${({ size, theme }) =>
    isSmallOrTiny(size)
      ? theme.textStyles.labelM
      : theme.textStyles.labelLEmphasized};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  outline: 1px solid transparent;

  padding: 0 ${({ icon }) => (icon === 'icon only' ? 0 : '1rem')};
  height: ${({ size }) => `${sizeToPx(size)}px`};
  border-radius: ${({ rounded, size }) =>
    rounded ? `${sizeToPx(size) / 2}px` : size === 'extratiny' ? '4px' : '8px'};
  width: ${({ icon, size }) =>
    icon === 'icon only' ? `${sizeToPx(size)}px` : 'auto'};
  flex-shrink: ${({ icon }) => (icon === 'icon only' ? 0 : 'initial')};

  transition: ${({ theme }) => theme.transitions.fast};

  background: none;
  color: ${({ theme }) => theme.colors.blueDark100};
  border: 2px solid ${({ theme }) => theme.colors.blueDark100};
  path {
    fill: ${({ theme }) => theme.colors.blueDark100};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.monochrome20};
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.blueLight30};
  }
  &:active,
  &[aria-expanded='true'] {
    background: ${({ theme }) => theme.colors.monochrome30};
  }
  &:disabled {
    background: none;
    border-color: ${({ theme }) => theme.colors.blueDark100};
    color: ${({ theme }) => theme.colors.blueDark100};
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    flex: 0 0 24px;

    ${({ icon, size }) => {
      const margin = size === 'large' ? '16px' : '12px';
      switch (icon) {
        case 'start':
          return { marginRight: margin };
        case 'end':
          return { marginLeft: margin };
        default:
          return {};
      }
    }}
  }
`;

export const ButtonSubtle = styled(ButtonSecondary)`
  border: 1px solid ${({ theme }) => theme.colors.monochrome40};

  &:hover {
    background: none;
    border-color: ${({ theme }) => theme.colors.blueLight100};
  }
  &:focus {
    outline: none;
  }
  &:focus-visible {
    box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.blueLight30};
  }
  &:active,
  &[aria-expanded='true'] {
    background: ${({ theme }) => theme.colors.monochrome20};
  }
  &:disabled {
    background: none;
    border-color: ${({ theme }) => theme.colors.monochrome40};
    color: ${({ theme }) => theme.colors.blueDark100};
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ButtonText = styled(ButtonSubtle)`
  border: none;
  outline: 1px solid transparent;

  &:hover {
    background: ${({ theme }) => theme.colors.monochrome30};
    path {
      fill: ${({ theme }) => theme.colors.blueDark100};
    }
  }
  &:active,
  &[aria-expanded='true'] {
    background: ${({ theme }) => theme.colors.monochrome40};
    path {
      fill: ${({ theme }) => theme.colors.blueDark100};
    }
  }
  &:disabled {
    background: none;
    border-color: ${({ theme }) => theme.colors.monochrome40};
    color: ${({ theme }) => theme.colors.blueDark100};
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ButtonPrimary = styled(ButtonSecondary, {
  shouldForwardProp: (prop) => prop !== 'isDanger',
})<{ isDanger?: boolean }>`
  border: none;
  outline: 1px solid transparent;

  background: ${({ theme, isDanger }) =>
    isDanger ? theme.colors.brandCoral100 : theme.colors.blueDark100};

  color: ${({ theme }) => theme.colors.monochrome10};

  path {
    fill: ${({ theme }) => theme.colors.monochrome10};
  }

  &:hover {
    color: ${({ theme }) => theme.colors.monochrome10};
    background: ${({ theme, isDanger }) =>
      isDanger ? theme.colors.brandCoral100 : theme.colors.blueDark120};
    path {
      fill: ${({ theme }) => theme.colors.monochrome10};
    }
  }
  &:focus {
    outline-color: transparent;
  }
  &:focus-visible {
    box-shadow: 0 0 0 4px ${({ theme }) => theme.colors.blueLight30};
  }
  &:active,
  &[aria-expanded='true'] {
    background: ${({ theme, isDanger }) =>
      isDanger ? theme.colors.brandCoral100 : theme.colors.blueDark140};
    path {
      fill: ${({ theme }) => theme.colors.monochrome10};
    }
  }
  &:disabled {
    color: ${({ theme }) => theme.colors.monochrome10};
    background: ${({ theme, isDanger }) =>
      isDanger ? theme.colors.brandCoral100 : theme.colors.blueDark100};
    opacity: 0.5;
    cursor: not-allowed;
    path {
      fill: ${({ theme }) => theme.colors.monochrome10};
    }
  }
`;

export const circleButtonSizes = ['small', 'large'] as const;
type CircleButtonSize = (typeof circleButtonSizes)[number];

interface CircleButtonProps {
  icon?: IconOption;
  size: CircleButtonSize;
}

function circleSizeToPx(size: CircleButtonSize): number {
  switch (size) {
    case 'small':
      return 40;

    case 'large':
      return 56;

    default:
      return assertNever(size);
  }
}

export const CircleButton = styled(ButtonSecondary)<CircleButtonProps>`
  padding: 0;
  width: ${({ size }) => circleSizeToPx(size)}px;
  height: ${({ size }) => circleSizeToPx(size)}px;
  flex: 0 0 ${({ size }) => circleSizeToPx(size)}px;
  border-radius: ${({ size }) => circleSizeToPx(size) / 2}px;

  &:hover,
  &:active,
  &[aria-expanded='true'] {
    background: ${({ theme }) => theme.colors.blueDark100};
    path {
      fill: ${({ theme }) => theme.colors.monochrome10};
    }
  }
  &:active,
  &[aria-expanded='true'] {
    background: ${({ theme }) => theme.colors.blueDark120};
  }
  &:disabled {
    background: none;
    path {
      fill: ${({ theme }) => theme.colors.blueDark100};
    }
  }
`;

export const ButtonDanger = styled(ButtonSecondary)`
  border-color: ${({ theme }) => theme.colors.temperatureHot100};
  color: ${({ theme }) => theme.colors.temperatureHot100};
`;
